import { Link, NavLink, useNavigate } from "react-router-dom";
import { useState, useEffect } from "react";
import { useAuth } from "@/context/AuthContext";
import { useTheme } from "@/context/ThemeContext";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator, DropdownMenuLabel } from "@/components/ui/dropdown-menu";
import NotificationsBell from "@/components/NotificationsBell";
import AvatarTiered from "@/components/AvatarTiered";
import { api } from "@/lib/api";
import { LogOut, User, LayoutDashboard, Leaf, Settings as SettingsIcon, Trophy, Sun, Moon, MessageCircle, Users as UsersIcon, ShieldCheck, ScanLine } from "lucide-react";

const links = [
  { to: "/proposals", label: "Proposals" },
  { to: "/learn", label: "Learn" },
  { to: "/companies", label: "Companies" },
  { to: "/rooms", label: "Rooms" },
  { to: "/updates", label: "Updates" },
  { to: "/leaderboard", label: "Leaderboard" },
];

export default function Nav() {
  const { user, logout } = useAuth();
  const { theme, toggle } = useTheme();
  const nav = useNavigate();
  const [isAdmin, setIsAdmin] = useState(false);
  const [reportsOpen, setReportsOpen] = useState(0);

  useEffect(() => {
    if (!user) { setIsAdmin(false); setReportsOpen(0); return; }
    api.get("/admin/whoami").then((r) => setIsAdmin(!!r.data?.is_admin)).catch(() => setIsAdmin(false));
  }, [user]);

  useEffect(() => {
    if (!isAdmin) return;
    const load = () => api.get("/admin/reports", { params: { status: "open" } })
      .then((r) => setReportsOpen(r.data?.open_count || 0))
      .catch(() => {});
    load();
    const t = setInterval(load, 20000);
    return () => clearInterval(t);
  }, [isAdmin]);

  return (
    <header className="sticky top-0 z-50 border-b border-border/70 bg-background/90 backdrop-blur">
      <div className="mx-auto flex max-w-7xl items-center justify-between px-6 py-4">
        <Link to="/" data-testid="nav-logo" className="flex items-center gap-2">
          <div className="flex h-8 w-8 items-center justify-center border border-primary bg-primary/10">
            <Leaf className="h-4 w-4 text-primary" strokeWidth={2.5} />
          </div>
          <div className="leading-tight">
            <div className="font-black tracking-tighter text-lg">ECO CHAMBER</div>
            <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Citizen Science Network</div>
          </div>
        </Link>

        <nav className="hidden gap-1 md:flex">
          {links.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              data-testid={`nav-${l.label.toLowerCase()}`}
              className={({ isActive }) =>
                `px-3 py-2 text-xs uppercase tracking-widest transition-colors border border-transparent ${
                  isActive ? "text-primary border-primary/40" : "text-muted-foreground hover:text-foreground"
                }`
              }
            >
              {l.label}
            </NavLink>
          ))}
        </nav>

        <div className="flex items-center gap-2">
          <button
            data-testid="nav-theme-toggle"
            onClick={toggle}
            aria-label="Toggle theme"
            className="flex h-9 w-9 items-center justify-center border border-border hover:border-primary/60 hover:text-primary"
          >
            {theme === "dark" ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
          </button>

          {user && <NotificationsBell />}

          {user ? (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button data-testid="nav-user-menu" className="flex items-center gap-2 border border-border px-3 py-1.5 hover:border-primary/50">
                  <AvatarTiered user={user} size="xs" />
                  <span className="hidden sm:inline text-sm">{user.display_name}</span>
                  <span className="mono text-xs text-primary flex items-center gap-0.5"><Trophy className="h-3 w-3" />{user.points ?? 0}</span>
                  {user.qualified && <Badge className="hidden sm:inline-flex bg-primary text-primary-foreground text-[9px] tracking-widest uppercase">Verified</Badge>}
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <DropdownMenuLabel className="text-xs uppercase tracking-widest text-muted-foreground">{user.role}</DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => nav(`/u/${user.id}`)} data-testid="nav-my-profile"><User className="mr-2 h-4 w-4" />My profile</DropdownMenuItem>
                <DropdownMenuItem onClick={() => nav("/messages")} data-testid="nav-messages"><MessageCircle className="mr-2 h-4 w-4" />Messages</DropdownMenuItem>
                <DropdownMenuItem onClick={() => nav("/friends")} data-testid="nav-friends"><UsersIcon className="mr-2 h-4 w-4" />Friends</DropdownMenuItem>
                <DropdownMenuItem onClick={() => nav("/dashboard")} data-testid="nav-dashboard"><LayoutDashboard className="mr-2 h-4 w-4" />Creator dashboard</DropdownMenuItem>
                <DropdownMenuItem onClick={() => nav("/leaderboard")} data-testid="nav-menu-leaderboard"><Trophy className="mr-2 h-4 w-4" />Leaderboard</DropdownMenuItem>
                <DropdownMenuItem onClick={() => nav("/settings")} data-testid="nav-settings"><SettingsIcon className="mr-2 h-4 w-4" />Settings</DropdownMenuItem>
                <DropdownMenuItem onClick={() => nav("/verify")} data-testid="nav-verify"><ScanLine className="mr-2 h-4 w-4" />Verify a report</DropdownMenuItem>
                {isAdmin && <DropdownMenuItem onClick={() => nav("/admin/codes")} data-testid="nav-admin-codes"><ShieldCheck className="mr-2 h-4 w-4" />Admin · codes</DropdownMenuItem>}
                {isAdmin && <DropdownMenuItem onClick={() => nav("/admin/users")} data-testid="nav-admin-users"><ShieldCheck className="mr-2 h-4 w-4" />Admin · users</DropdownMenuItem>}
                {isAdmin && <DropdownMenuItem onClick={() => nav("/admin/bugs")} data-testid="nav-admin-bugs"><ShieldCheck className="mr-2 h-4 w-4" />Admin · bugs</DropdownMenuItem>}
                {isAdmin && (
                  <DropdownMenuItem onClick={() => nav("/admin/reports")} data-testid="nav-admin-reports">
                    <ShieldCheck className="mr-2 h-4 w-4" />Admin · reports
                    {reportsOpen > 0 && <span data-testid="nav-admin-reports-badge" className="ml-auto mono text-[10px] bg-primary text-primary-foreground px-1.5">{reportsOpen}</span>}
                  </DropdownMenuItem>
                )}
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => { logout(); nav("/"); }} data-testid="menu-logout"><LogOut className="mr-2 h-4 w-4" />Sign out</DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          ) : (
            <>
              <Button variant="ghost" onClick={() => nav("/login")} data-testid="nav-login">Sign in</Button>
              <Button onClick={() => nav("/register")} data-testid="nav-register" className="bg-primary text-primary-foreground hover:bg-primary/90 rounded-none">Join</Button>
            </>
          )}
        </div>
      </div>
    </header>
  );
}
